/**
 * Взято отсюда и немного переписано
 * https://ru.stackoverflow.com/questions/418991/css-cubic-bezier-javascript-function
 */
const DEFAULT_DURATION = 400;

function solveEpsilon(duration: number) {
    return 1.0 / (200.0 * duration);
}

export type IBezierFunc = (x: number, duration: number) => number;

function cubicBezier(p1x: number, p1y: number, p2x: number, p2y: number): IBezierFunc {

    const cx = 3.0 * p1x;
    const bx = 3.0 * (p2x - p1x) - cx;
    const ax = 1.0 - cx -bx;
    const cy = 3.0 * p1y;
    const by = 3.0 * (p2y - p1y) - cy;
    const ay = 1.0 - cy - by;

    function sampleCurveX(t: number) {
        return ((ax * t + bx) * t + cx) * t;
    }

    function sampleCurveY(t: number) {
        return ((ay * t + by) * t + cy) * t;
    }

    function sampleCurveDerivativeX(t: number) {
        return (3.0 * ax * t + 2.0 * bx) * t + cx;
    }

    function solveCurveX(x: number, epsilon: number) {
        let t0, t1, t2, x2, d2, i;

        for (t2 = x, i = 0; i < 8; i++) {
            x2 = sampleCurveX(t2) - x;
            if (Math.abs(x2) < epsilon) {
                return t2;
            }
            d2 = sampleCurveDerivativeX(t2);
            if (Math.abs(d2) < 1e-6) {
                break;
            }
            t2 = t2 - x2 / d2;
        }

        t0 = 0.0;
        t1 = 1.0;
        t2 = x;

        if (t2 < t0) {
            return t0;
        }
        if (t2 > t1) {
            return t1;
        }

        while (t0 < t1) {
            x2 = sampleCurveX(t2);
            if (Math.abs(x2 - x) < epsilon) {
                return t2;
            }
            if (x > x2) {
                t0 = t2;
            } else {
                t1 = t2;
            }
            t2 = (t1 - t0) * 0.5 + t0;
        }

        return t2;
    }

    function solve(x: number, epsilon: number) {
        return sampleCurveY(solveCurveX(x, epsilon));
    }

    return function(x, duration = DEFAULT_DURATION) {
        return solve(x, solveEpsilon(+duration));
    };
}

export function animate(duration: number, func: IBezierFunc, callback: (x: number, y: number) => void): Promise<void> {
    const start = Date.now();
    return new Promise((resolve) => {
        function onFrame() {
            const delta = Date.now() - start;
            const x = delta < duration
                ? (delta / duration)
                : 1;
            callback(x, func(x, duration));
            if (x < 1) {
                window.requestAnimationFrame(onFrame);
            } else {
                resolve();
            }
        }
        window.requestAnimationFrame(onFrame);
    });
}

interface IScrollOption {
    x?: number;
    y?: number;
    duration?: number;
}

const SCROLL_DURATION = 250;

export function scrollTo(el: HTMLElement, options: IScrollOption) {
    const scrollStartY = el.scrollTop;
    const scrollStartX = el.scrollLeft;

    const scrollToX = options.x === undefined ? scrollStartX : options.x;
    const scrollToY = options.y === undefined ? scrollStartY : options.y;

    const scrollDeltaX = scrollToX - scrollStartX;
    const scrollDeltaY = scrollToY - scrollStartY;

    const duration = options.duration || SCROLL_DURATION;

    return animate(duration, Bezier.easeInOut, (x, y) => {
        el.scrollTo(
            scrollStartX + scrollDeltaX * y,
            scrollStartY + scrollDeltaY * y
        );
    });
}

export const Bezier = {
    easeInOut: cubicBezier(0.42, 0, 0.58, 1.0),
    easeOut: cubicBezier(0, 0, 0.58, 1.0),
    easeIn: cubicBezier(0.42, 0, 1.0, 1.0),
    ease: cubicBezier(0.25, 0.1, 0.25, 1.0),
    linear: cubicBezier(0.0, 0.0, 1.0, 1.0),
    cubicBezier: cubicBezier
};